import { useState, useEffect, useCallback, useRef } from 'react';
import { query, publicQuery } from '../graphql/client';
import { GET_FEED, GET_PUBLIC_FEED, GET_EXPLORE_FEED, GET_COMMITS_BY_TAG } from '../graphql/operations';
import { useAuth } from '../contexts/AuthContext';
import { getCached, setCache, getFeedCacheKey, getTagFeedCacheKey } from '../utils/cache';
import type { Commit, CommitConnection } from '../types/api';

interface UseFeedResult {
  commits: Commit[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  setCommits: React.Dispatch<React.SetStateAction<Commit[]>>;
}

export function useFeed(limit = 10): UseFeedResult {
  const { isAuthenticated } = useAuth();
  const cacheKey = getFeedCacheKey('main');
  const cached = getCached<Commit[]>(cacheKey);

  const [commits, setCommits] = useState<Commit[]>(cached || []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<Error | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Track separate tokens for each feed source (they use different pagination formats)
  const exploreTokenRef = useRef<string | undefined>();
  const personalTokenRef = useRef<string | undefined>();

  // Use ref to track if we have data (avoids dependency on commits state)
  const hasDataRef = useRef(!!cached?.length);

  const fetchFeed = useCallback(async (isPaginating = false, showLoading = true) => {
    // Only show loading if we don't have cached data
    if (showLoading && !hasDataRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      if (isAuthenticated) {
        // For authenticated users: merge global trending + personal feed (following + own)
        // When paginating, only fetch feeds that have more pages
        const shouldFetchExplore = !isPaginating || !!exploreTokenRef.current;
        const shouldFetchPersonal = !isPaginating || !!personalTokenRef.current;

        const [exploreData, personalData] = await Promise.all([
          shouldFetchExplore
            ? query<{ getExploreFeed: CommitConnection }>(GET_EXPLORE_FEED, {
                limit: limit + 5,
                nextToken: isPaginating ? exploreTokenRef.current : undefined,
              })
            : Promise.resolve({ getExploreFeed: { items: [], nextToken: null } }),
          shouldFetchPersonal
            ? query<{ getFeed: CommitConnection }>(GET_FEED, {
                limit: limit + 5,
                nextToken: isPaginating ? personalTokenRef.current : undefined,
              })
            : Promise.resolve({ getFeed: { items: [], nextToken: null } }),
        ]);

        // Update tokens for next pagination
        if (shouldFetchExplore) {
          exploreTokenRef.current = exploreData.getExploreFeed.nextToken || undefined;
        }
        if (shouldFetchPersonal) {
          personalTokenRef.current = personalData.getFeed.nextToken || undefined;
        }
        setHasMore(!!(exploreTokenRef.current || personalTokenRef.current));

        // Merge and dedupe by ID
        const allCommits = [...exploreData.getExploreFeed.items, ...personalData.getFeed.items];
        const seenIds = new Set<string>();
        const uniqueCommits = allCommits.filter((commit) => {
          if (seenIds.has(commit.id)) return false;
          seenIds.add(commit.id);
          return true;
        });

        // Sort by createdAt descending
        uniqueCommits.sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        const limitedCommits = uniqueCommits.slice(0, limit);

        if (isPaginating) {
          setCommits((prev) => {
            const combined = [...prev, ...limitedCommits];
            const seen = new Set<string>();
            return combined.filter((c) => {
              if (seen.has(c.id)) return false;
              seen.add(c.id);
              return true;
            });
          });
        } else {
          setCommits(limitedCommits);
          hasDataRef.current = limitedCommits.length > 0;
          // Cache the initial page
          setCache(cacheKey, limitedCommits);
        }
      } else {
        // For guests: just show global trending feed
        const data = await publicQuery<{ getExploreFeed: CommitConnection }>(GET_EXPLORE_FEED, {
          limit,
          nextToken: isPaginating ? exploreTokenRef.current : undefined,
        });

        exploreTokenRef.current = data.getExploreFeed.nextToken || undefined;
        setHasMore(!!exploreTokenRef.current);

        if (isPaginating) {
          setCommits((prev) => [...prev, ...data.getExploreFeed.items]);
        } else {
          setCommits(data.getExploreFeed.items);
          hasDataRef.current = data.getExploreFeed.items.length > 0;
          // Cache the initial page
          setCache(cacheKey, data.getExploreFeed.items);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch feed'));
    } finally {
      setLoading(false);
    }
  }, [limit, isAuthenticated, cacheKey]);

  useEffect(() => {
    // Only fetch once on mount
    if (!hasFetched) {
      setHasFetched(true);
      fetchFeed(false, !cached);
    }
  }, [hasFetched, fetchFeed, cached]);

  const loadMore = useCallback(async () => {
    if (hasMore) {
      await fetchFeed(true, false);
    }
  }, [fetchFeed, hasMore]);

  const refetch = useCallback(async () => {
    exploreTokenRef.current = undefined;
    personalTokenRef.current = undefined;
    setHasMore(false);
    await fetchFeed(false, false);
  }, [fetchFeed]);

  return {
    commits,
    loading,
    error,
    hasMore,
    loadMore,
    refetch,
    setCommits,
  };
}

export function usePublicFeed(limit = 10): UseFeedResult {
  const cacheKey = getFeedCacheKey('public');
  const cached = getCached<Commit[]>(cacheKey);

  const [commits, setCommits] = useState<Commit[]>(cached || []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<Error | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [hasFetched, setHasFetched] = useState(false);

  const hasDataRef = useRef(!!cached?.length);

  const fetchFeed = useCallback(async (token?: string, showLoading = true) => {
    if (showLoading && !hasDataRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      const data = await publicQuery<{ getPublicFeed: CommitConnection }>(GET_PUBLIC_FEED, {
        limit,
        nextToken: token,
      });

      if (token) {
        setCommits((prev) => [...prev, ...data.getPublicFeed.items]);
      } else {
        setCommits(data.getPublicFeed.items);
        hasDataRef.current = data.getPublicFeed.items.length > 0;
        setCache(cacheKey, data.getPublicFeed.items);
      }
      setNextToken(data.getPublicFeed.nextToken || undefined);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch public feed'));
    } finally {
      setLoading(false);
    }
  }, [limit, cacheKey]);

  useEffect(() => {
    if (!hasFetched) {
      setHasFetched(true);
      fetchFeed(undefined, !cached);
    }
  }, [hasFetched, fetchFeed, cached]);

  const loadMore = useCallback(async () => {
    if (nextToken) {
      await fetchFeed(nextToken, false);
    }
  }, [nextToken, fetchFeed]);

  const refetch = useCallback(async () => {
    setNextToken(undefined);
    await fetchFeed(undefined, false);
  }, [fetchFeed]);

  return {
    commits,
    loading,
    error,
    hasMore: !!nextToken,
    loadMore,
    refetch,
    setCommits,
  };
}

export function useExploreFeed(limit = 20): UseFeedResult {
  const cacheKey = getFeedCacheKey('explore');
  const cached = getCached<Commit[]>(cacheKey);

  const [commits, setCommits] = useState<Commit[]>(cached || []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<Error | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [hasFetched, setHasFetched] = useState(false);

  const hasDataRef = useRef(!!cached?.length);

  const fetchFeed = useCallback(async (token?: string, showLoading = true) => {
    if (showLoading && !hasDataRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      const data = await publicQuery<{ getExploreFeed: CommitConnection }>(GET_EXPLORE_FEED, {
        limit,
        nextToken: token,
      });

      if (token) {
        setCommits((prev) => [...prev, ...data.getExploreFeed.items]);
      } else {
        setCommits(data.getExploreFeed.items);
        hasDataRef.current = data.getExploreFeed.items.length > 0;
        setCache(cacheKey, data.getExploreFeed.items);
      }
      setNextToken(data.getExploreFeed.nextToken || undefined);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch explore feed'));
    } finally {
      setLoading(false);
    }
  }, [limit, cacheKey]);

  useEffect(() => {
    if (!hasFetched) {
      setHasFetched(true);
      fetchFeed(undefined, !cached);
    }
  }, [hasFetched, fetchFeed, cached]);

  const loadMore = useCallback(async () => {
    if (nextToken) {
      await fetchFeed(nextToken, false);
    }
  }, [nextToken, fetchFeed]);

  const refetch = useCallback(async () => {
    setNextToken(undefined);
    await fetchFeed(undefined, false);
  }, [fetchFeed]);

  return {
    commits,
    loading,
    error,
    hasMore: !!nextToken,
    loadMore,
    refetch,
    setCommits,
  };
}

export function useTagFeed(tag: string, limit = 10): UseFeedResult {
  const cacheKey = getTagFeedCacheKey(tag);
  const cached = getCached<Commit[]>(cacheKey);

  const [commits, setCommits] = useState<Commit[]>(cached || []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<Error | null>(null);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const [hasFetched, setHasFetched] = useState(false);

  const hasDataRef = useRef(!!cached?.length);

  const fetchFeed = useCallback(async (token?: string, showLoading = true) => {
    if (!tag) {
      setLoading(false);
      return;
    }

    if (showLoading && !hasDataRef.current) {
      setLoading(true);
    }
    setError(null);

    try {
      const data = await publicQuery<{ getCommitsByTag: CommitConnection }>(GET_COMMITS_BY_TAG, {
        tag,
        limit,
        nextToken: token,
      });

      if (token) {
        setCommits((prev) => [...prev, ...data.getCommitsByTag.items]);
      } else {
        setCommits(data.getCommitsByTag.items);
        hasDataRef.current = data.getCommitsByTag.items.length > 0;
        setCache(cacheKey, data.getCommitsByTag.items);
      }
      setNextToken(data.getCommitsByTag.nextToken || undefined);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch commits by tag'));
    } finally {
      setLoading(false);
    }
  }, [tag, limit, cacheKey]);

  useEffect(() => {
    if (!hasFetched && tag) {
      setHasFetched(true);
      fetchFeed(undefined, !cached);
    }
  }, [hasFetched, tag, fetchFeed, cached]);

  // Reset hasFetched when tag changes
  useEffect(() => {
    setHasFetched(false);
    hasDataRef.current = false;
  }, [tag]);

  const loadMore = useCallback(async () => {
    if (nextToken) {
      await fetchFeed(nextToken, false);
    }
  }, [nextToken, fetchFeed]);

  const refetch = useCallback(async () => {
    setNextToken(undefined);
    await fetchFeed(undefined, false);
  }, [fetchFeed]);

  return {
    commits,
    loading,
    error,
    hasMore: !!nextToken,
    loadMore,
    refetch,
    setCommits,
  };
}
